import React, {FC, useState} from "react";

import "./ContactListView.scss";
import classNames from "classnames";
import {ContentLocation} from "../../locations/ContentLocation";
import {ContactImage} from "./ContactImage";
import {StackPanel} from "../panels/StackPanel";
import {ContactViewModePref} from "../../util/Preferences";
import {ContactViewModes, ContactViewModeType} from "../../domain/ContactViewModeType";
import GridIcon from '@mui/icons-material/Apps';
import CategoryIcon from '@mui/icons-material/Category';
import {ViewModeSelector} from "../common/ViewModeSelector";
import {useAppSelector} from "../../hooks/ReduxHooks";
import {Accordion, AccordionDetails, AccordionSummary, Button} from "@mui/material";
import {getItemContent} from "../../state/ItemState";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {downloadFile} from "../../util/FileUtil";
import {EnrichedDavContact} from "../../services/messages/VCardContactData";

function sortContacts(a: EnrichedDavContact, b: EnrichedDavContact): number {
    return a.DisplayName.localeCompare(b.DisplayName);
}

const ContactCard: FC<{ contact: EnrichedDavContact, selected: boolean, showCategory?: boolean }> = ({contact, selected, showCategory}) => (
    <div key={contact.Id}
         className={classNames("card-small", {"selected": selected})}
         onClick={() => ContentLocation.showContact(contact.Id, undefined)}>

        <ContactImage contact={contact}/>

        <StackPanel orientation="vertical" className="overflow-hidden">
            <span className="name">{contact.DisplayName} </span>
            {/*<span className="detail">{contact.MobilePhone ?? contact.HomePhone ?? contact.BusinessPhone}</span>*/}
            <span className="detail">{contact.Emails[0]}</span>
            <span className="detail">{contact.PhoneNumbers[0]}</span>
            {showCategory && <span className="category">{contact.Category}</span>}
        </StackPanel>
    </div>
);

export const ContactListView: FC = () => {
    const davContacts = useAppSelector(s => s.contactState.davContacts);

    const selectedContact = useAppSelector(s => getItemContent(s.itemState) as EnrichedDavContact);

    const searchText = useAppSelector(s => s.contactState.contactSearchText).toLowerCase();
    const [viewMode, setViewMode] = useState(ContactViewModePref.get());

    function onSelectViewMode(newViewMode: ContactViewModeType) {
        ContactViewModePref.set(newViewMode);
        setViewMode(newViewMode);
    }

    function onExportContacts() {
        downloadFile("/Mail/Contact/Export", "Exported Contacts.vcf");
    }

    const filteredContacts = davContacts
        .filter(c => !searchText
            || (JSON.stringify(c).toLowerCase().includes(searchText)))
        .sort(sortContacts);

    let contactsView: React.ReactNode;

    switch (viewMode) {
        case "grid":
            contactsView = <div className="contact-grid-view">
                {filteredContacts.map(contact => (
                    <ContactCard key={contact.Id}
                                 contact={contact}
                                 showCategory
                                 selected={selectedContact?.Id === contact?.Id}/>
                ))}
            </div>
            break;
        case "byCategory":
            const contactsByCategory: Record<string, EnrichedDavContact[]> = {};
            for (const contact of filteredContacts) {
                const category = contact.Category ?? "Uncategorized";
                if (!contactsByCategory[category]) {
                    contactsByCategory[category] = [];
                }
                contactsByCategory[category].push(contact);
            }

            const sortedCategories = Object.entries(contactsByCategory).sort(([a], [b]) => a.localeCompare(b));
            contactsView = (
                <div className="contact-category-view">
                    {sortedCategories.map(([category, contacts]) => (
                        <Accordion key={category}>
                            <AccordionSummary>{category}</AccordionSummary>
                            <AccordionDetails>
                                <div className="contact-grid-view">
                                    {contacts.map(contact => <ContactCard key={contact.Id}
                                                                          contact={contact}
                                                                          selected={selectedContact?.Id === contact?.Id}/>)}
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </div>
            );
            break;
    }

    return (
        <div className="contact-view-container content-item-list">
            <ViewModeSelector defaultValue={viewMode}
                              options={ContactViewModes}
                              labels={[<GridIcon/>, <CategoryIcon/>]}
                              onSelectViewMode={onSelectViewMode}>
                <Button title="Export Contacts" onClick={onExportContacts}>
                    <FileUploadIcon/>
                </Button>
            </ViewModeSelector>

            {contactsView}
        </div>
    );
};
